import { Component, computed, input } from '@angular/core';
import { AccordionComponent } from '@content/ui/accordion';
import {
  computedContentSectionId,
  computedContentSectionTheme,
  ContentSectionComponent,
} from '@core/content-section';
import { InnogyComponentRendering } from '@core/jss-models';
import {
  getDropLinkObject,
  getFieldValue,
  getTreelistEntries,
} from '@core/jss-utils';
import {
  FaqStructuredData,
  StructuredDataModule,
  StructuredDataType,
} from '@core/structured-data';
import type { LinkField } from '@sitecore-jss/sitecore-jss-angular';

@Component({
  selector: 'wl-accordion-container',
  templateUrl: './accordion-container.component.html',
  standalone: true,
  imports: [AccordionComponent, StructuredDataModule, ContentSectionComponent],
})
export class AccordionContainerComponent {
  readonly rendering = input<InnogyComponentRendering>();
  readonly id = computedContentSectionId(this.rendering);
  readonly theme = computedContentSectionTheme(this.rendering);

  readonly items = computed(() =>
    getTreelistEntries(this.rendering()?.fields, 'Items')
  );

  readonly feedbackSettings = computed(() => {
    const feedBackSettings = getDropLinkObject(
      this.rendering()?.fields,
      'FeedbackSettings'
    );

    if (!feedBackSettings) {
      return undefined;
    }

    const contactLink = getFieldValue<LinkField>(
      feedBackSettings?.fields,
      'ContactUrl'
    );

    return {
      affirmativeFeedbackText: getFieldValue<string>(
        feedBackSettings,
        'AffirmativeFeedbackText'
      ),
      denyingFeedbackText: getFieldValue<string>(
        feedBackSettings,
        'DenyingFeedbackText'
      ),
      contactText: contactLink?.text,
      contactUrl: contactLink?.href,
      title: getFieldValue<string>(feedBackSettings, 'title'),
    };
  });

  readonly accordion = computed(() => ({
    title: getFieldValue<string>(this.rendering(), 'Title'),
    description: getFieldValue<string>(this.rendering(), 'Description'),
    accordionItems: this.items().map((item) => {
      const questionObject = getDropLinkObject(item.fields, 'Question');
      const question = getFieldValue<string>(questionObject, 'Question', '');
      const alternativeQuestion = getFieldValue<string>(
        item,
        'AlternativeQuestion',
        ''
      );
      const answer = getFieldValue<string>(item, 'Answer', '');

      return {
        question: alternativeQuestion || question,
        answer,
      };
    }),
  }));

  readonly structuredData = computed(
    () =>
      ({
        '@context': 'https://schema.org',
        '@type': StructuredDataType.FaqPage,
        mainEntity: this.accordion().accordionItems.map((faqItem) => ({
          '@type': StructuredDataType.Question,
          name: faqItem.question,
          acceptedAnswer: {
            '@type': StructuredDataType.Answer,
            text: faqItem.answer,
          },
        })),
      }) as FaqStructuredData
  );
}
